import React, { useMemo, useState } from 'react';
import WaButton from '../../wa/WaButton';
import {
	LOCATION_LABELS,
	formatDayKeyShort,
	overlapsWindows,
	teacherDayStanding,
	teacherMatchesLocation,
} from './schedulingModel';

/**
 * Coverage request decision screen — per-candidate context and a NAMED decision.
 *
 * Card note 7: the mockup credited the decision to "Beth (admin)", who does not
 * exist. The decider is whoever is signed in (Anthony or Grace), so the name is
 * read from the session rather than invented, and it is shown before the click
 * so nobody signs a decision they did not realise they were signing.
 */
function CoverageDecision({
	request,
	teachers,
	teacherNameById,
	blocks,
	timeOffDates,
	deciderName,
	busy,
	onDecide,
	onCancel,
}) {
	const [covererId, setCovererId] = useState('');
	const [note, setNote] = useState('');

	const requesterName = teacherNameById.get(request.teacherId) || request.teacherId;

	// The blocks this request actually covers — the day + location it names.
	const affected = useMemo(
		() => blocks.filter((block) => block.dayKey === request.date && block.location === request.location),
		[blocks, request.date, request.location]
	);
	const lessonCount = affected.reduce((sum, block) => sum + block.lessonCount, 0);

	/**
	 * Per-candidate context: is this teacher actually free that day, and what
	 * does taking it do to their load? Both are things a human would otherwise
	 * have to reconstruct by scrolling the board.
	 */
	const candidates = useMemo(() => {
		return teachers
			.filter((teacher) => teacher.active !== false && teacher.id !== request.teacherId)
			.map((teacher) => {
				const standing = teacherDayStanding(teacher, request.date);
				const clashes = [];

				if (timeOffDates?.has(`${teacher.id}::${request.date}`)) clashes.push('has the day off');
				if (standing.off) clashes.push('marked unavailable this weekday');
				if (!teacherMatchesLocation(teacher, request.location)) {
					clashes.push(`does not teach at ${LOCATION_LABELS[request.location] || request.location}`);
				}
				if (standing.blocks) {
					const hit = affected
						.flatMap((block) => block.events)
						.filter((event) => overlapsWindows(event.start, event.end, standing.blocks));
					if (hit.length > 0) clashes.push(`blocked over ${hit.length} of these lessons`);
				}

				// Already teaching elsewhere that day is a real clash, not a preference.
				const sameDayElsewhere = blocks.filter(
					(block) => block.dayKey === request.date && block.teacherId === teacher.id
				);
				const elsewhere = sameDayElsewhere.filter((block) => block.location !== request.location);
				if (elsewhere.length > 0) {
					clashes.push(
						`already at ${elsewhere.map((b) => LOCATION_LABELS[b.location] || b.location).join(', ')} that day`
					);
				}

				const currentLoad = blocks.filter((block) => block.teacherId === teacher.id).length;

				return {
					id: teacher.id,
					name: teacher.name || teacher.id,
					clashes,
					free: clashes.length === 0,
					currentLoad,
					loadAfter: currentLoad + affected.length,
				};
			})
			.sort((a, b) => {
				if (a.free !== b.free) return a.free ? -1 : 1;
				return a.currentLoad - b.currentLoad;
			});
	}, [teachers, request.teacherId, request.date, request.location, timeOffDates, affected, blocks]);

	const chosen = candidates.find((candidate) => candidate.id === covererId);

	return (
		<div className='ts-coverage'>
			<header className='ts-coverage__head'>
				<h3 className='ts-coverage__title'>Coverage request</h3>
				<span className='ts-chip is-coverage'>Open</span>
			</header>

			<div className='ts-coverage__ask'>
				<p className='ts-coverage__who'>
					<strong>{requesterName}</strong> — {formatDayKeyShort(request.date)} ·{' '}
					{LOCATION_LABELS[request.location] || request.location}
					{lessonCount > 0 && (
						<>
							{' · '}
							{lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
						</>
					)}
				</p>
				{request.reason && <p className='ts-coverage__reason'>“{request.reason}”</p>}
				{affected.length === 0 && (
					<p className='ts-coverage__warn'>
						No generated blocks match this day and location yet — approving will reassign
						nothing until the schedule covers that date.
					</p>
				)}
			</div>

			<fieldset className='ts-coverage__candidates'>
				<legend>Covering teacher</legend>
				{candidates.length === 0 ? (
					<p className='ts-coverage__warn'>No other active teacher is available to cover.</p>
				) : (
					candidates.map((candidate) => (
						<label
							key={candidate.id}
							className={`ts-candidate${candidate.free ? '' : ' is-clash'}${covererId === candidate.id ? ' is-selected' : ''}`}>
							<input
								type='radio'
								name='coverage-coverer'
								value={candidate.id}
								checked={covererId === candidate.id}
								onChange={() => setCovererId(candidate.id)}
								disabled={busy}
							/>
							<span className='ts-candidate__body'>
								<span className='ts-candidate__name'>{candidate.name}</span>
								<span className='ts-candidate__meta'>
									{candidate.free
										? `Free ${formatDayKeyShort(request.date).split(' ')[0]} · ${candidate.currentLoad} → ${candidate.loadAfter} blocks`
										: candidate.clashes.join(' · ')}
								</span>
							</span>
							{!candidate.free && <span className='ts-chip is-clash-chip'>Clashes</span>}
						</label>
					))
				)}
			</fieldset>

			<label className='ts-coverage__note'>
				<span>Note (kept with the decision)</span>
				<input
					type='text'
					value={note}
					onChange={(event) => setNote(event.target.value)}
					placeholder='Optional — visible to the teacher'
					disabled={busy}
				/>
			</label>

			<div className='ts-coverage__actions'>
				<WaButton
					variant='brand'
					onClick={() => onDecide(request, 'approve', { covererId, note })}
					disabled={busy || !covererId}>
					{chosen ? `Approve — reassign day to ${chosen.name}` : 'Approve'}
				</WaButton>
				<WaButton onClick={() => onDecide(request, 'decline', { covererId: null, note })} disabled={busy}>
					Decline with note
				</WaButton>
				<WaButton appearance='plain' onClick={onCancel} disabled={busy}>
					Cancel
				</WaButton>
				<p className='ts-coverage__decider'>
					Decided as <strong>{deciderName}</strong> — decisions are named, not anonymous.
				</p>
			</div>

			{chosen && affected.length > 0 && (
				<p className='ts-coverage__after'>
					After approval the {formatDayKeyShort(request.date)} card reads{' '}
					<strong>{chosen.name}</strong> with chip{' '}
					<span className='ts-chip is-manual'>Covering for {requesterName}</span> and stays
					locked.
				</p>
			)}
		</div>
	);
}

export default CoverageDecision;
